import { createContext, useContext, useEffect, useState } from 'react';
import { ShieldOff } from 'lucide-react';
import { getSession, signOut, type SessionUser } from '@/api/auth';
import { adminApi, ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SignInPage } from '@/auth/SignInPage';

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'forbidden'; user: SessionUser }
  | { status: 'ready'; user: SessionUser };

interface AuthCtx {
  user: SessionUser;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

/**
 * Two-step auth check:
 *   1. `/api/admin-auth/get-session` — are we signed in as backoffice staff?
 *   2. `/api/admin/orgs?limit=1` — can the staff session reach the backoffice API?
 *
 * A 401 means cookies expired. Anything else is a network/server error
 * that falls back to the sign-in screen for now.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  async function check() {
    setState({ status: 'loading' });
    try {
      const user = await getSession();
      if (!user) {
        setState({ status: 'unauthenticated' });
        return;
      }
      try {
        await adminApi.listOrgs({ limit: 1 });
        setState({ status: 'ready', user });
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setState({ status: 'unauthenticated' });
          return;
        }
        if (e instanceof ApiError && e.status === 403) {
          setState({ status: 'forbidden', user });
          return;
        }
        throw e;
      }
    } catch {
      setState({ status: 'unauthenticated' });
    }
  }

  useEffect(() => {
    void check();
  }, []);

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-svh items-center justify-center text-sm text-muted-foreground">
        Checking access…
      </div>
    );
  }

  if (state.status === 'unauthenticated') {
    return <SignInPage onSignedIn={check} />;
  }

  if (state.status === 'forbidden') {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-destructive text-destructive-foreground">
              <ShieldOff className="size-5" />
            </div>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>That is all.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              onClick={async () => {
                await signOut();
                await check();
              }}
            >
              Sign out
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Ctx.Provider
      value={{
        user: state.user,
        signOut: async () => {
          await signOut();
          await check();
        },
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthGate');
  }
  return ctx;
}
